import { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import {
  Sun, Moon, BookOpen, Award, Megaphone, Sparkles,
  Phone, ShieldAlert, CloudSun, CalendarDays, AlignLeft,
  Clock, BarChart2, Gauge, Image, Star, Wheat,
  ScrollText, BookText, MapPin,
} from 'lucide-react';
import AnimatedCounter from '../components/AnimatedCounter';

const ALL_WIDGETS = [
  {
    name: 'זמני תפילה', Icon: Sun, templates: 5, category: 'זמנים',
    emoji: '🕰️',
    description: 'מציג את הזמנים ההלכתיים לאורך היום — מעלות השחר ועד צאת הכוכבים — על ציר זמן חי המתעדכן בזמן אמת לפי מיקום בית הכנסת.',
    note: 'זמני תפילות מוזרמים אוטומטית מרכיביהם — אין צורך להגדיר כאן. הרכיב מתעדכן מדי יום ובכל יום חמישי טוען מראש את זמני השבת הקרובה.',
    whenToUse: 'רכיב עוגן שכדאי שיהיה בכל לוח — הבסיס שממנו שאר רכיבי הזמן יונקים את חישוביהם.',
    setupSteps: ['זמנים על הציר', 'זמנים נוספים'],
  },
  {
    name: 'שבת קודש', Icon: Moon, templates: 8, category: 'זמנים',
    emoji: '🕯️',
    description: 'מציג זמני כניסה ויציאת שבת — גם ליום שישי וגם ליום שבת — בתצוגה אחת. הנתונים מתרעננים אוטומטית מדי שבוע.',
    note: 'הרכיב מוצג כל ימות השבוע, לא רק בסוף שבוע. ניתן להוסיף דרשת ליל שבת לכל פרשה — תוצג אוטומטית בשבת המתאימה.',
    whenToUse: 'רכיב קבוע ושוטף שמשמש את הקהל לאורך כל השבוע.',
    setupSteps: ['כניסה ויציאה', 'תפילות שבת', 'דרשת ליל שבת'],
  },
  {
    name: 'תפילות', Icon: BookOpen, templates: 7, category: 'זמנים',
    emoji: '🤲',
    description: 'מציג לוח זמני תפילות לימות החול. מחשב עבור היום הנוכחי בלבד — תפילות יחסיות לזמנים הלכתיים מחושבות מחדש בכל יום. תפילות חוזרות מרחיבות לרשימת שעות בודדות.',
    note: 'ניתן להגדיר ימי עקיפה — שחרית ביום ראשון בשעה אחרת, זמן מיוחד לראש חודש — מבלי ליצור רכיב נפרד.',
    whenToUse: 'הרכיב המרכזי לכל בית כנסת לצרכי תפילות יום-יום.',
    setupSteps: ['הוספת תפילות'],
  },
  {
    name: 'פרנסים', Icon: Award, templates: 6, category: 'קהילה', advanced: true,
    emoji: '🕍',
    description: 'ניהול רשימת פרנסים עם שיוך יומי, שבועי, או חודשי. מציג בזמן אמת מי הפרנסים הפעילים כעת לפי התאריך העברי — כולל מעבר יום הלכתי אחרי שקיעה.',
    note: 'ניתן לשייך מספר פרנסים לאותו זמן כל עוד הם בסוגים שונים (יומי + שבועי + חודשי). לכל פרנס ניתן להוסיף טקסט הקדשה.',
    whenToUse: 'לניהול נציבות מאור, קפה, אחזקת בית המדרש, אבות ובנים, ועוד.',
    setupSteps: ['סוג פרנסות', 'ניהול פרנסים'],
  },
  {
    name: 'לוח מודעות', Icon: Megaphone, templates: 7, category: 'תוכן',
    emoji: '📋',
    description: 'לוח מודעות דיגיטלי לעדכונים לקהל. כשיש מודעות רבות — גולל אוטומטית בתנועה רציפה.',
    note: 'ניתן להגדיר לכל מודעה תאריך תפוגה — המודעה תיעלם אוטומטית כשהזמן יגיע.',
    whenToUse: 'לתקשורת שוטפת — שינויי זמנים זמניים, אירועים קרובים, הודעות דחופות.',
    setupSteps: ['הוספת מודעות'],
  },
  {
    name: 'שמחות', Icon: Sparkles, templates: 6, category: 'קהילה',
    emoji: '🎊',
    description: 'מציג שמחות ואירועי קהילה — חתונות, ברית מילה, בר/בת מצווה, אירוסין ועוד. כשיש שמחות רבות — גולל אוטומטית בתנועה חלקה.',
    note: 'ניתן להגדיר לכל שמחה תאריך תפוגה — תוסר אוטומטית לאחר האירוע.',
    whenToUse: 'ליצירת אווירה קהילתית ולשיתוף הציבור בשמחות בית הכנסת.',
    setupSteps: ['הוספת שמחות'],
  },
  {
    name: 'אנשי קשר', Icon: Phone, templates: 6, category: 'קהילה',
    emoji: '📞',
    description: 'ספר טלפונים של עובדי ופעילי הקהילה — תבניות מגוונות: רשימה, רשת, טבלה, קומפקטי, עמודות ניתנות להגדרה, וספר טלפונים מקובץ לפי תפקיד.',
    note: 'סטטי — יש לעדכן ידנית בכל שינוי תפקיד או מספר.',
    whenToUse: 'לקהילות גדולות עם מספר תפקידים שהציבור צריך להגיע אליהם.',
    setupSteps: ['הוספת אנשי קשר'],
  },
  {
    name: 'חירום', Icon: ShieldAlert, templates: 6, category: 'קהילה',
    emoji: '🚑',
    description: 'מציג רשימת כוננים ומתנדבי חירום לפי קטגוריות — חובשים, פרמדיקים, מע"ר. תבניות מגוונות: רשימה, כרטיסים, קבוצות, קומפקטי, עמודות ובאנרים.',
    note: 'סטטי לחלוטין — יש לעדכן ידנית כשמתנדב עוזב או מצטרף. מומלץ לעדכן מדי רבעון.',
    whenToUse: 'בכל בית כנסת עם נוכחות מתנדבי חירום.',
    setupSteps: ['הוספת מתנדבים'],
  },
  {
    name: 'מזג אוויר', Icon: CloudSun, templates: 2, category: 'אחר',
    emoji: '⛅',
    description: 'תחזית מזג אוויר לפי מיקום בית הכנסת. נשלף ממקור חיצוני ומתרענן כל 30 דקות.',
    note: 'תלוי במיקום שהוגדר בהגדרות הארגון. אין צורך בהגדרות נוספות — הכל אוטומטי.',
    whenToUse: 'תוספת שימושית לציבור בעת יציאה מהתפילה — במיוחד בחורף ובקיץ.',
    setupSteps: [],
  },
  {
    name: 'זמני מועד', Icon: CalendarDays, templates: 6, category: 'זמנים',
    emoji: '📅',
    description: 'מציג זמני כניסה ויציאה לימי מועד — פסח, סוכות, ראש השנה ועוד. הזמנים מחושבים אוטומטית מהיום שלפני כניסת המועד.',
    note: 'כל מועד ראוי למופע נפרד — רכיב אחד לפסח, נפרד לסוכות. כשמסתיים המועד, יש להסיר ידנית.',
    whenToUse: 'לתקופות חגים — להוסיף לפני החג ולהסיר לאחריו.',
    setupSteps: ['זמן כניסה', 'ימי המועד', 'זמן א׳ וזמן ב׳', 'תפילות מועד'],
  },
  {
    name: 'תוכן חופשי', Icon: AlignLeft, templates: 1, category: 'תוכן',
    emoji: '📝',
    description: 'תיבת טקסט להצגת תוכן קצר ונקודתי — פרשת שבוע, שעות פתיחה, ציטוט, מידע מוסדי.',
    note: 'לא מיועד למודעות! להודעות שוטפות — השתמש ברכיב "לוח מודעות". רכיב זה סטטי — מה שמקלידים נשאר עד לעריכה ידנית.',
    whenToUse: 'לתכנים קצרים שמתחלפים לאט — פרשת שבוע, שעות פתיחה, מידע מוסדי קבוע.',
    setupSteps: ['עריכת תוכן'],
  },
  {
    name: 'שעון', Icon: Clock, templates: 6, category: 'אחר',
    emoji: '🕐',
    description: 'שעון דיגיטלי או אנלוגי המציג את השעה המדויקת בזמן אמת — מתעדכן כל שנייה. האנלוגיים בעיצובים עשירים: מסגרות זהב, פנים שיש, פנים כחול עמוק.',
    note: 'רכיב תצוגה בלבד — אין להגדיר נתונים. צבע הרקע, הטקסט והגופן ניתנים לשינוי.',
    whenToUse: 'בכל לוח — שעון מציג נוכחות ומקצועיות. מומלץ כתוספת ויזואלית לפינות הלוח.',
    setupSteps: [],
  },
  {
    name: 'ניתוח הוצאות', Icon: BarChart2, templates: 2, category: 'כספים', advanced: true,
    emoji: '📊',
    description: 'גרף הוצאות לפי קטגוריות — עמודות או עוגה. שקיפות ויזואלית על חלוקת התקציב.',
    note: 'כרגע נתונים ידניים — יש לעדכן בכל תקופה. עובדים על חיבור אוטומטי למערכות כמו נדרים פלוס.',
    whenToUse: 'לדיווח שקוף לקהילה, ישיבות ועד, קמפיינים לגיוס תרומות.',
    setupSteps: ['קטגוריות הוצאות'],
  },
  {
    name: 'מדד כלכלי', Icon: Gauge, templates: 1, category: 'כספים', advanced: true,
    emoji: '💰',
    description: 'מציג את יחס ההכנסות מול ההוצאות בצורת מד — מיידי, ברור, ויזואלי.',
    note: 'כרגע נתונים ידניים — דורש עדכון תקופתי. עובדים על חיבור למערכות כמו נדרים פלוס.',
    whenToUse: 'לתצוגה תמציתית של הבריאות הכלכלית בישיבות או לוחות ציבוריים.',
    setupSteps: ['הכנסות', 'הוצאות'],
  },
  {
    name: 'תמונה / לוגו', Icon: Image, templates: 1, category: 'תוכן',
    emoji: '🖼️',
    description: 'מציג תמונה או לוגו — לוגו בית הכנסת, שלט שותפים, כרזת אירוע.',
    note: 'סטטי — לא מתחלף אוטומטית. לשינוי יש לערוך ידנית.',
    whenToUse: 'לזהות ויזואלית קבועה, או לפרסום עונתי שמחליפים ידנית.',
    setupSteps: ['בחירת תמונה'],
  },
  {
    name: 'נציב היום', Icon: Star, templates: 1, category: 'קהילה', advanced: true,
    emoji: '✨',
    description: 'מדגיש אדם מיוחד — תורם, מנציח, אדם לכבוד — עם שם, הקדשה, וסוג הנציבות.',
    note: 'אין רוטציה אוטומטית — כל שינוי נציב מצריך עדכון ידני.',
    whenToUse: 'לכבד תורמים, להנציח נפטרים ביום היארצייט, לסמן אירועים מיוחדים.',
    setupSteps: ['פרטי הנציב'],
  },
  {
    name: '\u05D6\u05DE\u05E0\u05D9 \u05D4\u05D9\u05D5\u05DD \u05D4\u05D4\u05DC\u05DB\u05EA\u05D9\u05D9\u05DD', Icon: Sun, templates: 8, category: '\u05D6\u05DE\u05E0\u05D9\u05DD',
    emoji: '\u2728',
    description: '\u05DE\u05E6\u05D9\u05D2 \u05D0\u05EA \u05D4\u05D6\u05DE\u05E0\u05D9\u05DD \u05D4\u05D4\u05DC\u05DB\u05EA\u05D9\u05D9\u05DD \u05DC\u05D0\u05D5\u05E8\u05DA \u05D4\u05D9\u05D5\u05DD \u2014 \u05DE\u05E2\u05DC\u05D5\u05EA \u05D4\u05E9\u05D7\u05E8 \u05D5\u05E2\u05D3 \u05D7\u05E6\u05D5\u05EA \u05D4\u05DC\u05D9\u05DC\u05D4 \u2014 \u05D1\u05E8\u05E9\u05D9\u05DE\u05D5\u05EA \u05E4\u05E9\u05D5\u05D8\u05D5\u05EA \u05D5\u05DE\u05DB\u05D5\u05D1\u05D3\u05D5\u05EA. \u05DB\u05D5\u05DC\u05DC \u05E1\u05E4\u05D9\u05E8\u05D4 \u05DC\u05D0\u05D7\u05D5\u05E8 \u05DC\u05D6\u05DE\u05DF \u05D4\u05E7\u05E8\u05D5\u05D1.',
    note: '\u05E0\u05D9\u05EA\u05DF \u05DC\u05D1\u05D7\u05D5\u05E8 \u05D0\u05D9\u05DC\u05D5 \u05D6\u05DE\u05E0\u05D9\u05DD \u05DC\u05D4\u05E6\u05D9\u05D2. \u05DB\u05D5\u05DC\u05DC 2 \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA \u05DC\u05E4\u05E1 \u05E8\u05D5\u05D7\u05D1\u05D9 \u05E2\u05DD \u05D2\u05DC\u05D9\u05DC\u05D4 \u05D0\u05D5\u05D8\u05D5\u05DE\u05D8\u05D9\u05EA.',
    whenToUse: '\u05DC\u05D4\u05E6\u05D2\u05EA \u05D6\u05DE\u05E0\u05D9\u05DD \u05D4\u05DC\u05DB\u05EA\u05D9\u05D9\u05DD \u05D8\u05D4\u05D5\u05E8\u05D9\u05DD \u05D1\u05DC\u05D9 \u05EA\u05E4\u05D9\u05DC\u05D5\u05EA \u05D5\u05DC\u05D5\u05F4\u05D6 \u05E7\u05D4\u05D9\u05DC\u05EA\u05D9.',
    setupSteps: ['\u05D1\u05D7\u05D9\u05E8\u05EA \u05D6\u05DE\u05E0\u05D9\u05DD', '\u05EA\u05D1\u05E0\u05D9\u05EA'],
  },
  {
    name: 'ספירת העומר', Icon: Wheat, templates: 6, category: 'זמנים',
    emoji: '🌾',
    description: 'מציג את ספירת העומר — מליל ט״ז ניסן ועד ליל שבועות — עם ברכה, ספירה מלאה בשבועות וימים, שמות הספירות הקבליות ופסוקים. מתעדכן אוטומטית בשקיעה.',
    note: 'הרכיב פעיל רק בין ט״ז ניסן ל-ה׳ סיון. מחוץ לתקופה מוצגת הודעה מתאימה — ניתן להשאיר בלוח כל השנה.',
    whenToUse: 'להוסיף בתחילת פסח. תומך בנוסחי אשכנז, ספרד ועדות המזרח.',
    setupSteps: ['הגדרות'],
  },
  {
    name: 'ענייני היום', Icon: ScrollText, templates: 11, category: 'זמנים',
    emoji: '📜',
    description: 'מציג את כל ענייני ההלכה של היום — תחנון, הלל, תוספות תפילה, מנהגים עונתיים, תקופות אבלות, קריאת התורה ועוד. מתעדכן אוטומטית בשקיעה ובחצות.',
    note: 'תומך בנוסחי אשכנז, ספרד ועדות המזרח. ניתן לבחור אילו קטגוריות להציג — תחנון, הלל, תוספות עונתיות, קריאת התורה, הפטרה ועוד.',
    whenToUse: 'רכיב יומי שמציג לקהל מה מיוחד בתפילות היום — מתאים לכל בית כנסת.',
    setupSteps: ['בחירת נוסח', 'קטגוריות להצגה'],
  },
  {
    name: 'לימודים יומיים', Icon: BookText, templates: 7, category: 'תוכן',
    emoji: '📖',
    description: 'מציג את סדרי הלימוד היומיים — דף יומי, משנה יומית, רמב"ם, נ"ך יומי, חפץ חיים, קיצור שולחן ערוך ועוד. כל סדר ניתן להפעלה ולכיבוי בנפרד.',
    note: 'תומך ביותר מ-15 סדרי לימוד שונים. מתעדכן אוטומטית לפי הלוח העברי — בשקיעה ובחצות.',
    whenToUse: 'לכל בית כנסת עם שיעורים יומיים — הקהל רואה בדיוק מה לומדים היום.',
    setupSteps: ['בחירת סדרי לימוד'],
  },
  {
    name: 'מפת מושבים', Icon: MapPin, templates: 2, category: 'קהילה',
    emoji: '💺',
    description: 'מפה ויזואלית של מושבי בית הכנסת — שורות, מקומות, ציוני דרך ושמות. מאפשר הצגת תפוסה ושיוך מקומות לחברי הקהילה.',
    note: 'ניתן לערוך את מבנה המפה — להוסיף שורות, מושבים וציוני דרך דרך עורך ויזואלי.',
    whenToUse: 'להצגת סידור המושבים בבית הכנסת ולניהול חלוקת מקומות.',
    setupSteps: ['עריכת מפה'],
  },
];

const CATEGORIES = [
  { key: 'all',   label: 'הכל' },
  { key: 'זמנים', label: 'זמנים' },
  { key: 'קהילה', label: 'קהילה' },
  { key: 'תוכן',  label: 'תוכן' },
  { key: 'כספים', label: 'כספים' },
  { key: 'אחר',   label: 'אחר' },
];

const CATEGORY_COLORS = {
  'זמנים': { bg: 'rgba(154,114,9,0.12)',  border: 'rgba(154,114,9,0.35)',  text: '#9a7209',  label: 'זמנים' },
  'קהילה': { bg: 'rgba(44,74,110,0.12)',  border: 'rgba(44,74,110,0.35)',  text: '#2c4a6e',  label: 'קהילה' },
  'תוכן':  { bg: 'rgba(45,90,61,0.12)',   border: 'rgba(45,90,61,0.35)',   text: '#2d5a3d',  label: 'תוכן' },
  'כספים': { bg: 'rgba(120,60,170,0.1)',  border: 'rgba(120,60,170,0.3)',  text: '#783caa',  label: 'כספים' },
  'אחר':   { bg: 'rgba(80,100,120,0.1)',  border: 'rgba(80,100,120,0.3)',  text: '#506478',  label: 'אחר' },
};

const AllWidgetsSection = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [hoveredWidget, setHoveredWidget] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const filtered = activeCategory === 'all'
    ? ALL_WIDGETS
    : ALL_WIDGETS.filter(w => w.category === activeCategory);

  const handleCategoryClick = useCallback((key) => {
    setVisible(false);
    setTimeout(() => {
      setActiveCategory(key);
      setVisible(true);
    }, 50);
  }, []);

  return (
    <Box
      id="widgets"
      ref={sectionRef}
      sx={{
        bgcolor: '#fcfbfa',
        py: { xs: 10, md: 14 },
        px: { xs: 3, md: 6 },
      }}
    >
      <Box sx={{ maxWidth: '1100px', mx: 'auto' }}>

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography sx={{
            fontFamily: 'Secular One, sans-serif',
            color: '#9a7209',
            fontSize: '0.85rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            mb: 2,
          }}>
            קטלוג מלא
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Frank Ruhl Libre, serif',
              color: '#1e3a5f',
              fontSize: { xs: '1.9rem', md: '2.5rem' },
              fontWeight: 900,
              letterSpacing: '-0.02em',
              mb: 2,
            }}
          >
            <AnimatedCounter target={21} suffix="+ רכיבים" /> ·{' '}
            <AnimatedCounter target={105} suffix="+ תבניות" />
          </Typography>
          <Typography sx={{
            color: '#5a6978',
            fontFamily: 'Heebo, sans-serif',
            fontSize: '1rem',
            maxWidth: '500px',
            mx: 'auto',
            lineHeight: 1.8,
          }}>
            לכל צורך קהילתי — מזמני תפילה ועד ניהול תקציב.
            <br />
            כל רכיב עם מספר תבניות עיצוב לבחירה.
          </Typography>
          <Typography sx={{
            color: '#9a7209',
            fontFamily: 'Heebo, sans-serif',
            fontSize: '0.88rem',
            mt: 1.5,
          }}>
            זו רק ההתחלה — רכיבים ותבניות חדשות מתווספות כל הזמן
          </Typography>
        </Box>

        {/* Category filter */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mb: 6, flexWrap: 'wrap' }}>
          {CATEGORIES.map(cat => (
            <Chip
              key={cat.key}
              label={cat.label}
              clickable
              onClick={() => handleCategoryClick(cat.key)}
              sx={{
                fontFamily: 'Heebo, sans-serif',
                fontSize: '0.85rem',
                fontWeight: activeCategory === cat.key ? 600 : 400,
                bgcolor: activeCategory === cat.key ? '#1e3a5f' : 'white',
                color: activeCategory === cat.key ? 'white' : '#5a6978',
                border: `1px solid ${activeCategory === cat.key ? '#1e3a5f' : 'rgba(30,58,95,0.15)'}`,
                boxShadow: activeCategory === cat.key ? '0 4px 12px rgba(30,58,95,0.2)' : 'none',
                transition: 'all 0.2s ease',
                height: 36,
                px: 0.5,
                '&:hover': {
                  bgcolor: activeCategory === cat.key ? '#152a47' : 'rgba(30,58,95,0.06)',
                },
              }}
            />
          ))}
        </Box>

        {/* Widget grid */}
        <Box
          className={`lp-stagger-container ${visible ? 'lp-visible' : ''}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 2,
          }}
        >
          {filtered.map((w) => {
            const colors = CATEGORY_COLORS[w.category];
            const IconComp = w.Icon;
            const isHovered = hoveredWidget === w.name;

            /* ── Shared sub-components ── */
            const CardHeader = () => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  flexShrink: 0, width: 38, height: 38, borderRadius: 1.5,
                  bgcolor: colors.bg, border: `1px solid ${colors.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <IconComp size={18} color={colors.text} strokeWidth={1.75} />
                </Box>
                <Typography sx={{ fontFamily: 'Secular One, sans-serif', color: '#1e3a5f', fontSize: '0.95rem', flex: 1 }}>
                  {w.name}
                </Typography>
              </Box>
            );

            const CardDescription = () => (
              <Typography sx={{ color: '#5a6978', fontFamily: 'Heebo, sans-serif', fontSize: '0.85rem', lineHeight: 1.7, flex: 1 }}>
                {w.description}
              </Typography>
            );

            const CardTemplates = () => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 'auto', pt: 0.5, borderTop: '1px solid rgba(30,58,95,0.06)' }}>
                <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: colors.text, opacity: 0.7 }} />
                <Typography sx={{ color: colors.text, fontFamily: 'Heebo, sans-serif', fontSize: '0.75rem', fontWeight: 600, opacity: 0.85 }}>
                  {w.templates} {w.templates === 1 ? 'תבנית עיצוב' : 'תבניות עיצוב'}
                </Typography>
              </Box>
            );

            const ExpandedContent = () => (
              <Box sx={{ mt: 1.5 }}>
                {w.note && (
                  <Box sx={{ borderRight: '3px solid #9a6200', bgcolor: 'rgba(154,114,9,0.06)', borderRadius: '0 6px 6px 0', p: 1.5, mb: 1 }}>
                    <Typography sx={{ fontFamily: 'Secular One, sans-serif', color: '#9a6200', fontSize: '0.72rem', mb: 0.5 }}>שים לב</Typography>
                    <Typography sx={{ fontFamily: 'Heebo, sans-serif', color: '#5a4010', fontSize: '0.82rem', lineHeight: 1.6 }}>{w.note}</Typography>
                  </Box>
                )}
                {w.whenToUse && (
                  <Box sx={{ borderRight: '3px solid #1e3a5f', bgcolor: 'rgba(30,58,95,0.05)', borderRadius: '0 6px 6px 0', p: 1.5, mb: 1 }}>
                    <Typography sx={{ fontFamily: 'Secular One, sans-serif', color: '#1e3a5f', fontSize: '0.72rem', mb: 0.5 }}>מתי להשתמש?</Typography>
                    <Typography sx={{ fontFamily: 'Heebo, sans-serif', color: '#3a4e65', fontSize: '0.82rem', lineHeight: 1.6 }}>{w.whenToUse}</Typography>
                  </Box>
                )}
                {w.setupSteps?.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                    <Typography sx={{ fontFamily: 'Secular One, sans-serif', color: '#5a6978', fontSize: '0.7rem' }}>שלבי הגדרה:</Typography>
                    {w.setupSteps.map((step, si) => (
                      <Box key={si} sx={{ fontSize: '0.7rem', fontFamily: 'Heebo, sans-serif', color: '#1e3a5f', bgcolor: 'rgba(30,58,95,0.07)', border: '1px solid rgba(30,58,95,0.15)', borderRadius: '20px', px: 1, py: 0.25 }}>
                        {step}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            );

            return (
              /* Grid anchor — holds space, never moves */
              <Box
                key={w.name}
                className="lp-stagger-item"
                sx={{ position: 'relative', zIndex: isHovered ? 200 : 1 }}
                onMouseEnter={() => setHoveredWidget(w.name)}
                onMouseLeave={() => setHoveredWidget(null)}
              >
                {/* Base card — always visible, fades when overlay takes over */}
                <Box sx={{
                  bgcolor: 'white', borderRadius: 2.5, p: 2.5,
                  border: '1px solid rgba(30,58,95,0.08)',
                  boxShadow: '0 1px 4px rgba(30,58,95,0.06)',
                  display: 'flex', flexDirection: 'column', gap: 1.25,
                  opacity: isHovered ? 0 : 1,
                  transition: 'opacity 0.15s ease',
                  pointerEvents: 'none',
                }}>
                  <CardHeader />
                  <CardDescription />
                  <CardTemplates />
                </Box>

                {/* Inflated overlay — balloon from center, no layout impact */}
                {isHovered && (
                  <Box sx={{
                    position: 'absolute',
                    top: '-10px', left: '-10px', right: '-10px',
                    zIndex: 200,
                    bgcolor: 'white',
                    borderRadius: 3,
                    p: 2.5,
                    border: '1.5px solid rgba(154,114,9,0.45)',
                    boxShadow: '0 0 0 1px rgba(154,114,9,0.2), 0 24px 64px rgba(30,58,95,0.26), 0 0 44px rgba(154,114,9,0.20)',
                    animation: 'inflateCard 0.25s ease-out',
                    transformOrigin: 'center center',
                    display: 'flex', flexDirection: 'column', gap: 1.25,
                  }}>
                    <CardHeader />
                    <CardDescription />
                    <CardTemplates />
                    <ExpandedContent />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Bottom note */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography dir="rtl" sx={{
            color: '#5a6978',
            fontFamily: 'Heebo, sans-serif',
            fontSize: '0.9rem',
            lineHeight: 1.8,
            direction: 'rtl',
          }}>
            כל הרכיבים זמינים בכל המסלולים — כולל תקופת הניסיון החינמית.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AllWidgetsSection;
